/**
 * Создание и редактирование проекта: название, описание (можно вставить текст из заявки), рубрика, плашка,
 * хэштеги, особенности стиля. Под формой — превью «рамки» поста: рубрика → текст → плашка → хэштеги.
 */
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { Project } from "@impuls/shared";
import { api, errText } from "../lib/api";
import { useSettings } from "../lib/queries";
import { Modal, useConfirm, useToast } from "./ui";
import { VkPreview } from "./VkPreview";

const FIELDS = ["name", "description", "rubric", "footer", "hashtags", "style_notes"] as const;
type Field = (typeof FIELDS)[number];
type Draft = Record<Field, string>;

const LIMITS: Record<Field, number> = { name: 120, description: 20000, rubric: 300, footer: 2000, hashtags: 500, style_notes: 5000 };

const BODY_PLACEHOLDER = "Здесь будет текст поста — его напишет Claude по вашему рассказу.";

/** Как на сервере: каждый хэштег с #, через пробел. */
export function projectsNormalizeTags(s: string): string {
  return s
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => (t.startsWith("#") ? t : `#${t}`))
    .join(" ");
}

/** Подсказка, если ссылку на сообщество записали не так, как понимает ВКонтакте. */
function footerWarning(footer: string): string {
  if (/\[[^\]\n]+\]\(https?:[^)]+\)/.test(footer))
    return "ВКонтакте не понимает ссылки вида [текст](адрес). Запишите так: [club123|текст].";
  if (/\[(?:club|public|id|event)\d+\]/.test(footer))
    return "У ссылки нет текста. Добавьте его после черты: [club123|Фондом президентских грантов].";
  if (/\[(?:club|public|id|event)\d+\|[^\]\n]*$/m.test(footer)) return "Ссылка не закрыта: в конце нужна квадратная скобка «]».";
  return "";
}

function draftOf(p: Project | null): Draft {
  return {
    name: p?.name ?? "",
    description: p?.description ?? "",
    rubric: p?.rubric ?? "",
    footer: p?.footer ?? "",
    hashtags: p?.hashtags ?? "",
    style_notes: p?.style_notes ?? "",
  };
}

export function ProjectsFormModal({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: settings } = useSettings();
  const formId = useId();
  const initial = useMemo(() => draftOf(project), [project]);
  const [d, setD] = useState<Draft>(initial);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // окно уже открыто (эффект Modal срабатывает раньше) — ставим курсор в название
    nameRef.current?.focus({ preventScroll: true });
  }, []);

  const set = (f: Field) => (e: { target: { value: string } }) => setD((x) => ({ ...x, [f]: e.target.value }));
  const dirty = FIELDS.some((f) => d[f] !== initial[f]);
  const nameError = d.name.trim() ? "" : "Укажите название проекта — так он будет называться в списках и в редакторе.";
  const showNameError = tried && !!nameError;
  const warn = footerWarning(d.footer);
  const tags = projectsNormalizeTags(d.hashtags);

  const close = async () => {
    if (saving) return;
    if (dirty) {
      const ok = await confirm({
        title: "Закрыть без сохранения?",
        text: project ? "Изменения в проекте пропадут." : "Новый проект не будет создан, введённое пропадёт.",
        confirm: "Закрыть без сохранения",
        danger: true,
      });
      if (!ok) return;
    }
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (nameError) {
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError("");
    const body = Object.fromEntries(FIELDS.map((f) => [f, d[f].trim()]));
    try {
      if (project) await api.put<Project>(`/api/projects/${project.id}`, body);
      else await api.post<Project>("/api/projects", body);
      void qc.invalidateQueries({ queryKey: ["projects"] });
      toast(project ? "Проект сохранён" : `Проект «${d.name.trim()}» создан`);
      onClose();
    } catch (err) {
      setError(errText(err));
      setSaving(false);
    }
  };

  const frame = [d.rubric.trim(), BODY_PLACEHOLDER, d.footer.trim(), tags].filter(Boolean).join("\n\n");

  return (
    <Modal
      open
      wide
      onClose={() => void close()}
      title={project ? "Изменить проект" : "Новый проект"}
      labelledBy={`${formId}-title`}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={() => void close()} disabled={saving}>
            Отмена
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving || (!!project && !dirty)}>
            {saving ? "Сохраняю…" : project ? "Сохранить" : "Создать проект"}
          </button>
        </>
      }
    >
      <form id={formId} className="proj-form" onSubmit={submit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor={`${formId}-name`}>
            Название <span className="proj-form__req">обязательно</span>
          </label>
          <input
            ref={nameRef}
            id={`${formId}-name`}
            className="input"
            value={d.name}
            onChange={set("name")}
            maxLength={LIMITS.name}
            placeholder="Например: Первая помощь: я могу! Новые школы"
            aria-invalid={showNameError || undefined}
            aria-describedby={showNameError ? `${formId}-name-err` : undefined}
            aria-required="true"
          />
          {showNameError && (
            <span className="proj-form__err" id={`${formId}-name-err`}>
              {nameError}
            </span>
          )}
        </div>

        <FieldBox label="Описание" hint="Цели, участники, сроки — можно из заявки.">
          {(a) => (
            <textarea
              {...a}
              className="textarea proj-form__desc"
              value={d.description}
              onChange={set("description")}
              rows={7}
              maxLength={LIMITS.description}
            />
          )}
        </FieldBox>

        <FieldBox label="Рубрика" hint="Первая строка поста, слово в слово.">
          {(a) => (
            <input
              {...a}
              className="input"
              value={d.rubric}
              onChange={set("rubric")}
              maxLength={LIMITS.rubric}
              placeholder="🚑 Проект «…»"
            />
          )}
        </FieldBox>

        <FieldBox
          label="Плашка"
          hint={
            <>
              Конец поста, слово в слово. Ссылка на сообщество — <span className="proj-form__code">[club123|текст]</span>
            </>
          }
          warn={warn}
        >
          {(a) => <textarea {...a} className="textarea" value={d.footer} onChange={set("footer")} rows={3} maxLength={LIMITS.footer} />}
        </FieldBox>

        <FieldBox label="Хэштеги" hint="Через пробел, решётка не обязательна.">
          {(a) => (
            <input
              {...a}
              className="input"
              value={d.hashtags}
              onChange={set("hashtags")}
              maxLength={LIMITS.hashtags}
              placeholder="#ПерваяПомощь #ИмпульсЛангепас"
            />
          )}
        </FieldBox>

        <FieldBox label="Особенности стиля" hint="Например: посты про смены — короткие посты-эмоции.">
          {(a) => (
            <textarea
              {...a}
              className="textarea"
              value={d.style_notes}
              onChange={set("style_notes")}
              rows={3}
              maxLength={LIMITS.style_notes}
            />
          )}
        </FieldBox>

        <section className="proj-frame" aria-labelledby={`${formId}-frame`}>
          <div className="proj-frame__head">
            <h4 id={`${formId}-frame`}>Рамка поста</h4>
          </div>
          <div className="proj-frame__desk">
            <VkPreview text={frame} images={[]} groupName={settings?.vk_group_name || "Центр безопасности «Импульс»"} />
          </div>
        </section>

        {error && (
          <div className="error-line" role="alert">
            <TriangleAlert size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
      </form>
    </Modal>
  );
}

type FieldProps = { id: string; "aria-describedby": string };

function FieldBox({
  label,
  hint,
  warn,
  children,
}: {
  label: ReactNode;
  hint: ReactNode;
  warn?: string;
  children: (p: FieldProps) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, "aria-describedby": `${id}-hint` })}
      <span className="field__hint" id={`${id}-hint`}>
        {hint}
        {warn && (
          <span className="proj-form__warn" role="status">
            <TriangleAlert size={14} aria-hidden="true" />
            {warn}
          </span>
        )}
      </span>
    </div>
  );
}
